import { useId, type CSSProperties } from 'react';
import type { Sector } from '../variants/c/sectors';
import type { Callout } from './data';

// Pieces shared by the default screens and the composed owner pages.

export function Brand({ s, tag }: { s: Sector; tag?: string }) {
  return (
    <div className="op-brand">
      <span className="op-brand-mark">{s.mark}</span>
      <span className="op-brand-name">{s.maker}</span>
      {tag && <span className="op-tag">{tag}</span>}
    </div>
  );
}

export function Spark({ series }: { series: number[] }) {
  const W = 92;
  const H = 30;
  const max = Math.max(...series, 0.001);
  const pts = series.map((v, i) => [(i / (series.length - 1)) * W, H - 3 - (v / max) * (H - 8)] as const);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const [lx, ly] = pts[pts.length - 1];
  const id = `op-sg${useId().replace(/:/g, '')}`;
  return (
    <svg className="op-spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--op-al)" stopOpacity=".42" />
          <stop offset="1" stopColor="var(--op-al)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0.33, 0.66].map((g) => (
        <line key={g} x1="0" x2={W} y1={H * g} y2={H * g} stroke="var(--op-grid, rgba(255,255,255,.06))" strokeWidth="1" />
      ))}
      <path d={`${line} L${W} ${H} L0 ${H} Z`} fill={`url(#${id})`} />
      <path className="op-spark-line" d={line} fill="none" stroke="var(--op-al)" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" pathLength={1} />
      <circle cx={lx} cy={ly} r="2.4" fill="var(--op-dot, #fff)" />
      <circle cx={lx} cy={ly} r="5" fill="var(--op-al)" opacity=".25" />
    </svg>
  );
}

// ---------- The product hero with thin callout lines to live values ----------
export const HW = 242;
const HH = 150;

export function Hero({ s, callouts, pos }: { s: Sector; callouts: Callout[]; pos?: string }) {
  return (
    <div className="op-hero" key={s.key}>
      <div className="op-hero-img">
        <img src={s.product} alt="" width={484} height={336} loading="lazy" style={pos ? { objectPosition: pos } : undefined} />
      </div>
      <svg className="op-lines" width={HW} height={HH} viewBox={`0 0 ${HW} ${HH}`} aria-hidden="true">
        {callouts.map((c, i) => {
          const bottom = i > 1;
          const [px, py] = c.at ?? [50, 50];
          const ax = Math.round((px / 100) * HW) + 0.5;
          const ay = (py / 100) * HH;
          const ly = bottom ? HH - 3 : 3;
          return (
            <g key={c.k} style={{ '--d': `${0.12 + i * 0.09}s` } as CSSProperties}>
              <path className="op-line" d={`M${ax} ${ly} L${ax} ${ay}`} fill="none" stroke="var(--op-ln, rgba(255,255,255,.55))" strokeWidth=".75" pathLength={1} />
              <circle className="op-node" cx={ax} cy={ay} r="2.2" fill="var(--op-dot, #fff)" />
              <circle className="op-node" cx={ax} cy={ay} r="5" fill="none" stroke="var(--op-ln2, rgba(255,255,255,.35))" strokeWidth=".75" />
            </g>
          );
        })}
      </svg>
      {callouts.map((c, i) => {
        const ax = Math.round(((c.at ?? [50, 50])[0] / 100) * HW);
        const side = i % 2 === 1 ? { left: ax + 6 } : { right: HW - ax + 6 };
        return (
          <div key={c.k} className={`op-co op-co-${i}`} style={{ ...side, '--d': `${0.2 + i * 0.09}s` } as unknown as CSSProperties}>
            <span>{c.k}</span>
            <b>{c.v}</b>
          </div>
        );
      })}
    </div>
  );
}
